// MaterialsListDialog.tsx — Concrete volume, weight, CG, area & embed materials summary
// Calls materialsCalc to compute totals; offers CSV download

import DialogBase from './DialogBase';
import MaterialListTable from '../components/MaterialListTable';
import { useShallowStore } from '../store/useStore';
import { calcMaterials, calcProjectTotals, type MaterialsResult } from '../utils/materialsCalc';
import { gatedDownload } from '../utils/exportGateway';
import { useEntitlement } from '../auth/AuthProvider';

const csvEncoder = new TextEncoder();

export default function MaterialsListDialog() {
  const { project, ui } = useShallowStore(s => ({ project: s.project, ui: s.ui }));
  const { refresh } = useEntitlement();
  if (!project) return null;

  const isMetric = ui.units === 'metric';
  const panels = project.panels;
  const allResults: MaterialsResult[] = panels.map(p => calcMaterials(p));
  const totals = calcProjectTotals(allResults);

  function buildCSVBytes(): Uint8Array {
    const header = 'Mark,Width,Height,Thickness,Concrete (yd³),Concrete (m³),Weight (kips),Weight (kg),CG X,CG Y,Ext Face (sf),Finished (sf),H/T,Load/Pick (kips),Load/Brace (kips),Braces,Picks,Welds\n';
    const rows   = panels.map((p, i) => {
      const r = allResults[i];
      const m = p.mpvar;
      return [
        m.panelMark, m.width, m.height, m.thickness,
        r.concreteCuYd.toFixed(3), r.concreteCuM.toFixed(3),
        r.panelWeightKips.toFixed(1), Math.round(r.panelWeightKg),
        r.cgX.toFixed(2), r.cgY.toFixed(2),
        r.exteriorFaceAreaSqFt.toFixed(1), r.finishedAreaSqFt.toFixed(1),
        r.slendernessRatio.toFixed(1),
        r.pickCount > 0 ? (r.craneLoadPerPickLb / 1000).toFixed(1) : '',
        r.braceCount > 0 ? (r.braceLoadPerPointLb / 1000).toFixed(1) : '',
        r.braceCount, r.pickCount, r.weldCount,
      ].join(',');
    }).join('\n');
    return csvEncoder.encode(header + rows);
  }

  async function exportCSV() {
    const filename = (project?.name ?? 'materials').replace(/\s+/g, '_') + '_materials.csv';
    const res = await gatedDownload({
      artifact: 'csv',
      filename,
      mimeType: 'text/csv',
      build: buildCSVBytes,
    });
    if (res.ok) {
      refresh();
    } else if (res.reason === 'producer_error') {
      alert(res.message ?? 'CSV export failed.');
    } else if (res.reason === 'network') {
      alert(`Export failed: ${res.message ?? 'network error'}`);
    }
  }

  return (
    <DialogBase title="Materials List" okLabel="Close" cancelLabel="" onOk={() => {}}>
      <div className="mat-summary">
        <div className="mat-row">
          <span>Total concrete</span>
          <strong>{isMetric ? `${totals.totalCuM.toFixed(3)} m³` : `${totals.totalCuYd.toFixed(3)} yd³`}</strong>
        </div>
        <div className="mat-row">
          <span>Total weight</span>
          <strong>{isMetric ? `${Math.round(totals.totalWeightKg).toLocaleString()} kg` : `${totals.totalWeightTon.toFixed(1)} tons`}</strong>
        </div>
        <div className="mat-row">
          <span>Total finished area</span>
          <strong>{isMetric ? `${totals.totalFinishedSqM.toFixed(1)} m²` : `${totals.totalFinishedSqFt.toFixed(0)} sq ft`}</strong>
        </div>
        <div className="mat-row">
          <span>Brace points</span>
          <strong>{totals.totalBraces}</strong>
        </div>
        <div className="mat-row">
          <span>Pick points</span>
          <strong>{totals.totalPicks}</strong>
        </div>
        <div className="mat-row">
          <span>Weld connections</span>
          <strong>{totals.totalWelds}</strong>
        </div>
      </div>

      <MaterialListTable panels={panels} isMetric={isMetric} activePanelId={ui.activePanelId} />

      <div className="dialog-footer-extra">
        <button className="dialog-btn" onClick={() => { void exportCSV(); }}>Export CSV (1 token)</button>
      </div>
    </DialogBase>
  );
}
